import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const GRADES = [
    { stars: '☆☆☆☆☆☆', label: '초심자', emoji: '🌱', starCount: 0 },
    { stars: '★☆☆☆☆☆', label: '입문자', emoji: '📖', starCount: 1 },
    { stars: '★★☆☆☆☆', label: '수련자', emoji: '🧘', starCount: 2 },
    { stars: '★★★☆☆☆', label: '리모트 뷰어', emoji: '👁️', starCount: 3 },
    { stars: '★★★★☆☆', label: '각성자', emoji: '✨', starCount: 4 },
    { stars: '★★★★★☆', label: '초능력자', emoji: '🔮', starCount: 5 },
    { stars: '★★★★★★', label: '시간 여행자', emoji: '⏳', starCount: 6 }
];

// Card image name helper (same as App.js)
const getCardImageName = (number) => {
    if (number === 41) return 'Swords14.png';
    if (number === 42) return 'Cups13.png';
    if (number === 43) return 'Wands12.png';
    if (number === 44) return 'Pentacles11.png';
    if (number === 45) return '21-TheWorld.png';
    const suitMap = { 0: 'Swords', 1: 'Cups', 2: 'Wands', 3: 'Pentacles' };
    const suitIndex = Math.floor((number - 1) / 10);
    const numberInSuit = (number - 1) % 10 + 1;
    const suit = suitMap[suitIndex];
    const numStr = numberInSuit === 1 ? '01' : String(numberInSuit).padStart(2, '0');
    return `${suit}${numStr}.png`;
};

const RemoteViewing = ({ majorMeanings }) => {
    const [gameState, setGameState] = useState('idle'); // 'idle', 'playing', 'finished'
    const [targetCards, setTargetCards] = useState([]);
    const [selectedCards, setSelectedCards] = useState([]);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [nickname, setNickname] = useState(() => sessionStorage.getItem('rv_nickname') || '');
    const [showRanking, setShowRanking] = useState(false);
    const [rankings, setRankings] = useState([]);
    const [comment, setComment] = useState('');
    const [resultData, setResultData] = useState(null);
    const [showMethodModal, setShowMethodModal] = useState(false);
    const [cardAnimationPhase, setCardAnimationPhase] = useState('none'); // 'dealing', 'done'
    const [isSaving, setIsSaving] = useState(false); // Prevent duplicate saves
    const [isSharing, setIsSharing] = useState(false);
    const [isLoadingRank, setIsLoadingRank] = useState(false);
    
    // 추가: 선택 카드 덱 상태 및 셔플 애니메이션 상태
    const [selectionDeck, setSelectionDeck] = useState(Array.from({ length: 45 }, (_, i) => i + 1));
    const [isSelectionShuffling, setIsSelectionShuffling] = useState(false);

    const startTimeRef = useRef(null);
    const timerIntervalRef = useRef(null);
    const topSectionRef = useRef(null);
    const resultSectionRef = useRef(null);

    // Scroll Effect
    useEffect(() => {
        if (gameState === 'playing') {
            setTimeout(() => {
                topSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        } else if (gameState === 'finished') {
            setTimeout(() => {
                if (resultSectionRef.current) {
                    const yOffset = -150; 
                    const y = resultSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }, 100);
        }
    }, [gameState]);

    // Timer Effect
    useEffect(() => {
        if (gameState === 'playing' && startTimeRef.current) {
            timerIntervalRef.current = setInterval(() => {
                setElapsedTime((Date.now() - startTimeRef.current) / 1000);
            }, 100);
        }
        return () => {
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        };
    }, [gameState]);

    // Format time for display (fixed width to prevent jitter)
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        const tenths = Math.floor((seconds * 10) % 10);
        if (mins > 0) {
            return `${mins}:${String(secs).padStart(2, '0')}.${tenths}`;
        }
        return `${String(secs).padStart(2, '0')}.${tenths}`;
    };

    const handleStartGame = () => {
        const trimmedNick = nickname.trim();
        if (!trimmedNick) {
            alert('닉네임을 입력해주세요.');
            return;
        }
        sessionStorage.setItem('rv_nickname', trimmedNick);

        // Generate 6 random unique target cards (1-45)
        const deck = Array.from({ length: 45 }, (_, i) => i + 1);
        const shuffled = [...deck].sort(() => Math.random() - 0.5);
        const targets = shuffled.slice(0, 6);

        setTargetCards(targets);
        setSelectedCards([]);
        setResultData(null);
        setComment('');
        setElapsedTime(0);
        setIsSaving(false); // Reset save state
        startTimeRef.current = Date.now();

        // 선택 카드 셔플
        const shuffledSelection = [...deck].sort(() => Math.random() - 0.5);
        setSelectionDeck(shuffledSelection);
        setIsSelectionShuffling(true);

        // Card dealing animation
        setCardAnimationPhase('dealing');
        setTimeout(() => {
            setCardAnimationPhase('done');
            setIsSelectionShuffling(false);
        }, 1000); // 1.0초로 설정

        setGameState('playing');
    };

    const handleCardSelect = (num) => {
        if (gameState !== 'playing') return;
        if (selectedCards.includes(num)) {
            setSelectedCards(selectedCards.filter(n => n !== num));
        } else if (selectedCards.length < 6) {
            setSelectedCards([...selectedCards, num]);
        }
    };

    const handleFinishGame = async () => {
        if (selectedCards.length !== 6) {
            alert('6장의 카드를 모두 선택해주세요.');
            return;
        }

        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        const finalTime = (Date.now() - startTimeRef.current) / 1000;

        if (finalTime < 2.0) {
            alert('비정상적인 플레이가 감지되었습니다. 게임이 초기화됩니다.');
            setGameState('idle');
            return;
        }

        // Calculate score (permutation match)
        let matches = 0;
        for (let i = 0; i < 6; i++) {
            if (targetCards[i] === selectedCards[i]) {
                matches++;
            } else {
                break;
            }
        }

        const gradeInfo = GRADES[matches];
        const result = {
            nickname: nickname.trim(),
            score: matches,
            stars: gradeInfo.stars,
            grade: `${gradeInfo.emoji} ${gradeInfo.label}`,
            play_time: parseFloat(finalTime.toFixed(2)),
            comment: '',
            timestamp: new Date().toLocaleString('ko-KR'),
            ip_country: '🇰🇷'
        };

        setResultData(result);
        setElapsedTime(finalTime);
        setGameState('finished');
    };

    const handleSaveResult = async () => {
        if (!resultData || isSaving) return;

        setIsSaving(true); // Prevent duplicate clicks
        const dataToSend = { ...resultData, comment: comment.slice(0, 50) };

        try {
            const response = await fetch(`${API_URL}/api/remote-viewing/rank`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });
            if (response.ok) {
                alert('결과가 저장되었습니다!');
                fetchRankings();
                setShowRanking(true);
            } else {
                const err = await response.json();
                alert(`저장 실패: ${err.detail || '알 수 없는 오류'}`);
                setIsSaving(false); // Allow retry on error
            }
        } catch (error) {
            console.error('저장 오류:', error);
            alert('서버 연결 오류');
            setIsSaving(false); // Allow retry on error
        }
    };

    const handleShareResult = async () => {
        if (isSharing || !resultData) return;
        setIsSharing(true);

        try {
            const element = resultSectionRef.current;
            if (!element) return;

            const canvas = await html2canvas(element, {
                backgroundColor: '#1a0a2a',
                scale: 2
            });

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    alert('이미지 생성 실패');
                    setIsSharing(false);
                    return;
                }

                const file = new File([blob], 'rv_result.png', { type: 'image/png' });
                const shareData = {
                    title: '리모트 뷰잉 결과',
                    text: `[${resultData.nickname}]님의 리모트 뷰잉 기록: ${resultData.score}개 적중! (${resultData.grade})`,
                    files: [file]
                };

                if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                    try {
                        await navigator.share(shareData);
                    } catch (err) {
                        if (err.name !== 'AbortError') console.error(err);
                    }
                } else {
                    try {
                        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                        alert('이미지가 클립보드에 복사되었습니다.');
                    } catch (err) {
                        const link = document.createElement('a');
                        link.href = canvas.toDataURL('image/png');
                        link.download = 'rv_result.png';
                        link.click();
                        alert('이미지가 다운로드되었습니다.');
                    }
                }
                setIsSharing(false);
            }, 'image/png');

        } catch (error) {
            console.error('캡처 오류:', error);
            alert('오류가 발생했습니다.');
            setIsSharing(false);
        }
    };

    const fetchRankings = async () => {
        setIsLoadingRank(true);
        try {
            const response = await fetch(`${API_URL}/api/remote-viewing/rank`);
            const data = await response.json();
            setRankings(data.rankings || []);
        } catch (error) {
            console.error('랭킹 조회 오류:', error);
        } finally {
            setIsLoadingRank(false);
        }
    };

    const getCardImagePath = (num) => {
        return `${process.env.PUBLIC_URL}/Cards-png/${getCardImageName(num)}`;
    };

    const getCardBackPath = () => {
        return `${process.env.PUBLIC_URL}/Cards-png/RVCardBack.png`;
    };

    // Handle nickname input (max 8 Korean chars)
    const handleNicknameChange = (e) => {
        const value = e.target.value;
        if (value.length <= 8) {
            setNickname(value);
        }
    };

    // Get ranking display for a record
    const getRankDisplay = (record, index, allRankings) => {
        if (record.score === 0) return '-';

        // Count how many have higher scores
        let rank = 1;
        for (const r of allRankings) {
            if (r.score > record.score) rank++;
            else if (r.score === record.score && r.play_time < record.play_time) rank++;
        }
        return rank;
    };

    return (
        <div className="remote-viewing-container">
            <p className="rv-hint-text">목표 카드를 마음속으로 집중하고 선택하세요.</p>

            {/* Target Cards Section */}
            <div className="rv-section" ref={topSectionRef}>
                <h3 className="rv-section-title">🎯 타겟 카드 (맞춰야 할 순서)</h3>
                <div className="rv-target-grid">
                    {gameState === 'idle' ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="rv-target-slot empty">
                                <span>?</span>
                            </div>
                        ))
                    ) : (
                        targetCards.map((num, i) => (
                            <div key={i} className={`rv-target-slot ${cardAnimationPhase === 'dealing' ? `dealing delay-${i}` : ''}`}>
                                <img src={getCardImagePath(num)} alt={`Target ${i + 1}`} className="rv-card-img" />
                                <span className="rv-target-order">{i + 1}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <hr className="rv-divider" />

            {/* Selection Cards Section - Card backs with selection overlay */}
            <div className="rv-section">
                <h3 className="rv-section-title">🃏 선택 카드 (6장을 순서대로 선택)</h3>
                {/* 셔플 애니메이션 클래스 적용 */}
                <div className={`rv-selection-grid ${isSelectionShuffling ? 'shuffling' : ''}`}>
                    {selectionDeck.map((num) => {
                        const selectionIndex = selectedCards.indexOf(num);
                        const isSelected = selectionIndex !== -1;
                        return (
                            <div
                                key={num}
                                className={`rv-select-card ${isSelected ? 'selected' : ''} ${gameState !== 'playing' ? 'disabled' : ''}`}
                                onClick={() => handleCardSelect(num)}
                            >
                                <img src={getCardBackPath()} alt="Card Back" className="rv-card-back-img" />
                                {isSelected && <span className="rv-select-order">{selectionIndex + 1}</span>}
                            </div>
                        );
                    })}
                </div>
            </div>

            <hr className="rv-divider" />

            {/* Control Buttons */}
            <div className="rv-controls">
                {(gameState === 'idle' || gameState === 'finished') && (
                    <div className="rv-start-form">
                        <input
                            type="text"
                            placeholder="닉네임"
                            value={nickname}
                            onChange={handleNicknameChange}
                            className="rv-nickname-input"
                            maxLength={8}
                        />
                        <button onClick={handleStartGame} className="rv-btn rv-btn-start">🚀 리모트 뷰잉 시작</button>
                    </div>
                )}
                {gameState === 'playing' && (
                    <div className="rv-playing-controls">
                        <div className="rv-timer">⏱️ {formatTime(elapsedTime)}s</div>
                        <button onClick={handleFinishGame} className="rv-btn rv-btn-finish">🛑 리모트 뷰잉 종료</button>
                    </div>
                )}
                <div className="rv-sub-controls">
                    <button onClick={() => setShowMethodModal(true)} className="rv-btn rv-btn-info" title="리모트 뷰잉이란?">❓ 게임 방법</button>
                    <button onClick={() => { fetchRankings(); setShowRanking(true); }} className="rv-btn rv-btn-rank">🏆 랭킹 보드</button>
                </div>
            </div>

            {/* Result Section */}
            {gameState === 'finished' && resultData && (
                <div className="rv-result-section" ref={resultSectionRef}>
                    <hr className="rv-divider" />
                    <h3 className="rv-section-title">📊 리모트 뷰잉 결과</h3>

                    {/* Show user's selected cards revealed */}
                    <div className="rv-result-comparison">
                        <p>당신의 선택:</p>
                        <div className="rv-result-cards">
                            {selectedCards.map((num, i) => {
                                const isMatch = targetCards[i] === num;
                                return (
                                    <div key={i} className={`rv-result-card ${isMatch ? 'match' : 'mismatch'}`}>
                                        <img src={getCardImagePath(num)} alt={`Selected ${i + 1}`} />
                                        <span className={`rv-result-badge ${isMatch ? 'match' : 'mismatch'}`}>{isMatch ? 'O' : 'X'}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="rv-result-details">
                        {resultData.score >= 1 && <h3 className="rv-congrats-msg">🎉 축하합니다!! 🎉</h3>}
                        <p><strong>{resultData.nickname}</strong> 님</p>
                        <p className="rv-result-stars">{resultData.stars}</p>
                        <p className="rv-result-grade">등급: {resultData.grade}</p>
                        <p>맞힌 카드: {resultData.score}개 / 6개</p>
                        <p>플레이 시간: {resultData.play_time}초</p>
                    </div>

                    <div className="rv-comment-section">
                        <textarea
                            maxLength={50}
                            placeholder="소감 한마디 (50자 이내)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className="rv-comment-textarea"
                            rows={2}
                        />
                        <div className="rv-action-buttons">
                            <button
                                onClick={handleSaveResult}
                                className="rv-btn rv-btn-save"
                                disabled={isSaving}
                            >
                                {isSaving ? '저장됨 ✓' : '💾 랭킹 등록'}
                            </button>
                            <button
                                onClick={handleShareResult}
                                className="rv-btn rv-btn-share"
                                disabled={isSharing}
                            >
                                {isSharing ? '생성 중...' : '📤 공유하기'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ranking Modal */}
            {showRanking && (
                <div className="rv-modal-overlay" onClick={() => setShowRanking(false)}>
                    <div className="rv-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>🏆 명예의 전당 (Top 1000)</h3>
                        <button onClick={() => setShowRanking(false)} className="rv-modal-close">✕</button>
                        <div className="rv-ranking-table-wrapper">
                            <table className="rv-ranking-table">
                                <thead>
                                    <tr><th>순위</th><th>닉네임</th><th>날짜</th><th>등급</th><th>별</th><th>시간</th><th>소감</th></tr>
                                </thead>
                                <tbody>
                                    {isLoadingRank ? (
                                        <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>⏳ 랭킹 데이터 로딩 중...</td></tr>
                                    ) : rankings.length === 0 ? (
                                        <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>아직 기록이 없습니다.</td></tr>
                                    ) : (
                                        rankings.map((r, i) => (
                                            <tr key={i}>
                                                <td className={r.score >= 1 ? 'gold-text' : ''}>{r.score === 0 ? '-' : getRankDisplay(r, i, rankings)}</td>
                                                <td>{r.nickname}</td>
                                                <td className="rv-date-cell">{r.timestamp}</td>
                                                <td>{r.grade}</td>
                                                <td>
                                                    {[...Array(6)].map((_, starIndex) => (
                                                        <span key={starIndex} className={starIndex < r.score ? 'gold-text' : 'muted-star'}>
                                                            {starIndex < r.score ? '★' : '☆'}
                                                        </span>
                                                    ))}
                                                </td>
                                                <td>{r.play_time}s</td>
                                                <td className="rv-comment-cell" title={r.comment}>{r.comment}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Game Method Modal */}
            {showMethodModal && (
                <div className="rv-modal-overlay" onClick={() => setShowMethodModal(false)}>
                    <div className="rv-modal rv-method-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>🧠 리모트 뷰잉이란?</h3>
                        <button onClick={() => setShowMethodModal(false)} className="rv-modal-close">✕</button>
                        <div className="rv-method-content">
                            <h3>🌌 리모트 뷰잉 : 타로 직관 게임<br/>(Remote Viewing: Tarot Intuition Game)</h3>
                            <p>
                                <strong>"시공간을 초월하는 직관의 힘"</strong><br/>
                                당신의 잠재된 초감각적 지각(ESP)을 깨울 준비가 되셨나요?<br/>
                                이곳은 당신의 직관이 우주의 질서와 공명하는 경이로운 순간을 포착하는 실험실입니다.<br/>
                                45장의 타로 카드 중 시스템이 숨겨둔 6장의 카드를 순서대로 찾아내세요.<br/>
                                확률은 상상을 초월합니다. 당신의 직관은 우주의 질서와 공명할 수 있을까요?
                            </p>

                            <h4>🧠 무엇을 하는 게임인가요?</h4>
                            <p>
                                이 프로젝트는 ‘리모트 뷰잉(Remote Viewing, 원격 투시)’ 개념에서 영감을 받아 설계된 체험형 직관 게임입니다.<br/>
                                리모트 뷰잉은 냉전 시대부터 초감각적 지각(ESP)을 군사/첩보 목적으로 활용하기 위해 연구되었던 주제입니다. 비록 과학적 합의에 도달하지 못했고 여전히 논쟁의 영역에 있지만, 우리는 이 흥미로운 개념을 빌려 당신의 순수한 직관과 집중력을 시험해 보고자 합니다.<br/>
                                논리적인 사고는 잠시 내려두세요. 이곳은 오직 당신의 <strong>'느낌'</strong>만이 나침반이 되는 곳입니다.
                            </p>

                            <h4>🎮 수행 프로토콜 (How to Play)</h4>
                            <ol>
                                <li><strong>타겟 생성 (Target Generation):</strong> '시작' 버튼을 누르면, 시스템은 45장의 타로 카드 중 무작위로 6장의 타겟을 선택하여 시공간 어딘가에 숨겨둡니다.</li>
                                <li><strong>직관 발동 (Intuition):</strong> 화면에는 45장의 카드가 뒷면으로 펼쳐집니다. 눈을 감고 심호흡을 하세요. 논리적 사고를 멈추고 오직 직관이 이끄는 대로 타겟과 동일한 카드를 <strong>1번부터 6번까지 순서대로</strong> 찾아내야 합니다. (선택 카드도 매번 섞입니다)</li>
                                <li><strong>검증 (Verification):</strong> 6장을 모두 선택 후 '종료'를 눌러 결과를 확인합니다. 정확한 카드와 <strong>순서(Sequence)</strong>까지 일치해야 정답으로 인정됩니다. 단 0.1초의 망설임도 당신의 기록이 됩니다.</li>
                            </ol>

                            <h4>📈 불가능에 가까운 도전 (Probability)</h4>
                            <ul>
                                <li>이 게임에서 6장의 카드를 순서대로 모두 맞출 확률은 <strong>약 58억 6천만 분의 1</strong>입니다.</li>
                                <li>이것은 <strong>로또 1등 당첨보다 720배 더 어려운</strong> 확률입니다.</li>
                                <li>번개를 7번 연속으로 맞거나, 해변의 모래알 중 특정 하나를 바로 집어낼 확률과 비슷합니다.</li>
                            </ul>
                            <p>따라서, <strong>단 하나의 별(★)이라도 얻었다면 당신은 이미 특별한 감각을 지닌 것입니다.</strong></p>

                            <h4>🏆 등급 가이드 (Result Class)</h4>
                            <ul>
                                <li>🌱 <strong>초심자 (0개):</strong> [97.8%] 지극히 정상적인 결과입니다. 우주는 넓고 기회는 무한합니다.</li>
                                <li>📖 <strong>입문자 (1개):</strong> [1/45] 우연과 필연의 경계입니다. 당신의 직관이 깨어나기 시작했습니다.</li>
                                <li>🧘 <strong>수련자 (2개):</strong> [1/1,980] 우연이라기엔 놀라운 집중력입니다. 깊은 잠재력이 꿈틀거립니다.</li>
                                <li>👁️ <strong>리모트 뷰어 (3개):</strong> [1/85,140] 현실의 장막 너머 숨겨진 타겟을 포착했습니다. 직관의 세계로 진입했습니다.</li>
                                <li>✨ <strong>각성자 (4개):</strong> [1/3,575,880] 번개를 맞을 확률보다 어렵습니다. 당신의 제3의 눈이 떠졌습니다.</li>
                                <li>🔮 <strong>초능력자 (5개):</strong> [1/146,611,080] 로또 1등보다 18배 어려운 확률. 인류의 한계를 넘어선 신비로운 존재입니다.</li>
                                <li>⏳ <strong>시간 여행자 (6개):</strong> [1/5,864,443,200] 58억 분의 1의 기적. 당신은 시공간을 여행하는 자입니다.</li>
                            </ul>

                            <h4>💡 팁 (Tips)</h4>
                            <ul>
                                <li><strong>첫 느낌을 믿으세요:</strong> 선택의 순간, 가장 먼저 떠오르는 이미지를 의심하지 말고 선택하세요. 당신의 직관은 생각보다 훨씬 강력합니다.</li>
                                <li><strong>이완된 집중:</strong> 마음을 비우고, 무의식의 목소리에 귀를 기울이세요. 카드를 뚫어지게 쳐다보기보다, 깊은 호흡으로 긴장을 풀고 멍하니 응시할 때 직관은 더 잘 발휘됩니다.</li>
                            </ul>

                            <div className="rv-disclaimer">
                                <h4>⚠️ Disclaimer</h4>
                                <p>본 게임은 엔터테인먼트(오락) 목적으로 제작되었습니다.<br/>
                                '리모트 뷰잉'과 초능력에 대한 내용은 게임의 몰입을 위한 테마일 뿐이며, 과학적으로 확립되거나 입증된 사실이 아닙니다. 이 게임의 결과가 사용자의 실제 초능력 유무를 판별하거나 미래를 예언하지 않습니다. 58억 분의 1이라는 통계적 희소성의 감각을 체험하고, 당신의 잠재된 직관을 즐겁게 테스트하는 용도로만 즐겨 주시기 바랍니다.</p>
                            </div>

                            <div className="rv-final-msg">
                                <p>맞추지 못하는 것이 정상입니다. 좌절하지 말고 당신의 직관을 훈련하세요.</p>
                                <p>오늘의 당신에게 필요한 카드는 정답 카드가 아니라, 당신이 뽑은 오답 카드일지도 모릅니다.</p>
                            </div>

                            <button onClick={() => setShowMethodModal(false)} className="rv-btn">닫기</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RemoteViewing;